// Copyright (c) 2023, Alix Mougenot (Au pied des Arbres), alix.mougenot@gmail.com
// See license file.
import type { InjectionKey, ComputedRef, Ref } from "vue";
import type {
  Actor,
  ActorCreation,
  ActorEdit,
  Case,
  CaseCreation,
  CaseEdit,
  CDOMember,
  Dentist,
  DentistEdit,
  Document,
  Event,
  EventCreation,
  EventEdit,
  HealthCenter,
  HealthCenterEdit,
  Import,
  Reference,
  Template,
  TemplateCategory,
  TemplateCreation,
  TemplateEdit,
  TemplateSubCategory,
} from "@/types";
import type { MutateResult } from "@vue/apollo-composable";

export const googleClientIDKey = Symbol() as InjectionKey<string>;
export const modalActiveKey = Symbol() as InjectionKey<Ref<boolean>>;
export const errorHandlerKey = Symbol() as InjectionKey<
  (error: string, detail?: Error) => void
>;

export const actorsKey = Symbol() as InjectionKey<
  ComputedRef<{ list: Actor[]; index: Map<string, number> }>
>;
export const dentistKey = Symbol() as InjectionKey<
  ComputedRef<{ list: Dentist[]; index: Map<string, number> }>
>;
export const filesKey = Symbol() as InjectionKey<
  ComputedRef<{ list: Case[]; index: Map<string, number> }>
>;
export const membersKey = Symbol() as InjectionKey<
  ComputedRef<{
    index: Map<string, CDOMember>;
    options: Array<{
      value: string;
      text: string;
    }>;
  }>
>;

export const importsKey = Symbol() as InjectionKey<ComputedRef<Import[]>>;

export const templatesKey = Symbol() as InjectionKey<
  ComputedRef<{
    list: Template[];
    categories: Record<
      TemplateCategory,
      { [_ in TemplateSubCategory]: Array<string> }
    >;
    index: Map<string, number>;
  }>
>;
export const healthCentersKey = Symbol() as InjectionKey<
  ComputedRef<{ list: HealthCenter[]; index: Map<string, number> }>
>;

export const mutateDentistKey = Symbol() as InjectionKey<
  (id: string, dentist: DentistEdit) => MutateResult<any>
>;

export const mutateCaseKey = Symbol() as InjectionKey<
  (id: string, edit: CaseEdit) => MutateResult<any>
>;
export const createCaseKey = Symbol() as InjectionKey<
  (creation: CaseCreation) => MutateResult<any>
>;

export const mutateActorKey = Symbol() as InjectionKey<
  (id: string, edit: ActorEdit) => MutateResult<any>
>;
export const createActorKey = Symbol() as InjectionKey<
  (creation: ActorCreation) => MutateResult<any>
>;

export const mutateHealthCenterKey = Symbol() as InjectionKey<
  (id: string, edit: HealthCenterEdit) => MutateResult<any>
>;

export const mutateEventKey = Symbol() as InjectionKey<
  (id: string, edit: EventEdit) => MutateResult<any>
>;
export const createEventKey = Symbol() as InjectionKey<
  (edit: EventCreation) => MutateResult<any>
>;

export const mutateTemplateKey = Symbol() as InjectionKey<
  (id: string, edit: TemplateEdit) => MutateResult<any>
>;
export const createTemplateKey = Symbol() as InjectionKey<
  (creation: TemplateCreation) => MutateResult<any>
>;

// Used by logic, not only data stores
export const protagonistsKey = Symbol() as InjectionKey<
  ComputedRef<{
    accused:
      | {
          found: (Dentist | Actor | HealthCenter)[];
          notFound: Reference[];
        }
      | undefined;
    plaignants:
      | {
          found: (Dentist | Actor | HealthCenter)[];
          notFound: Reference[];
        }
      | undefined;
  }>
>;

export const attachDocumentKey = Symbol() as InjectionKey<
  (event: Event, panelX: number) => Promise<Document | null>
>;

export const displayDocumentKey = Symbol() as InjectionKey<
  (
    document: Document,
    event: Event | null,
    panelX: number
  ) => Promise<Document | null>
>;
